import React, { useEffect, useRef, useState } from 'react';
import { Brain, Heart, Volume2, Check } from 'lucide-react';
import { solutionData } from '../../data/mock';
import learningPause from '@/assets/images/learning-to-pause.jpg';

const SolutionSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iconMap = { Brain, Heart, Volume2 };

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-[#202020] relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left Content */}
          <div>
            {/* Section Label */}
            <div className={`mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <span className="text-sm text-[#efede6]/40 uppercase tracking-widest">The Solution</span>
            </div>

            {/* Title */}
            <h2 className={`text-3xl md:text-4xl font-normal text-[#efede6] mb-8 leading-tight tracking-tight transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {solutionData.title}
            </h2>

            {/* Intro */}
            <p className={`text-[#efede6]/50 text-lg mb-10 leading-relaxed transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {solutionData.intro}
            </p>

            {/* Benefits */}
            <div className="space-y-5">
              {solutionData.benefits.map((benefit, index) => {
                const Icon = iconMap[benefit.icon];
                return (
                  <div
                    key={index}
                    className={`flex items-center gap-5 bg-[#efede6]/5 rounded-2xl p-5 border border-[#efede6]/10 transition-all duration-500 hover:bg-[#efede6]/10 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                    style={{ transitionDelay: `${(index + 3) * 150}ms` }}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#efede6]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#efede6]/70" />
                    </div>
                    <p className="text-[#efede6] text-lg">{benefit.text}</p>
                  </div>
                );
              })}
            </div>

            {/* Conclusion */}
            <div className={`mt-10 bg-[#efede6]/10 rounded-2xl p-8 border border-[#efede6]/10 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-start gap-4">
                <Check className="w-6 h-6 text-[#efede6]/70 flex-shrink-0 mt-1" />
                <p className="text-[#efede6] text-lg leading-relaxed">
                  {solutionData.conclusion}
                </p>
              </div>
            </div>
          </div>

          {/* Right - Emotional Journey Images */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative space-y-6">
              {/* Image 1: Contemplation */}
              <div className="relative rounded-3xl shadow-2xl">
                <img
                  src={learningPause}
                  alt="Parent in peaceful contemplation"
                  className="w-full h-[500px] object-cover grayscale brightness-90"
                />
                {/* <div className="absolute inset-0 bg-gradient-to-r from-[#202020]/60 to-transparent" /> */}
                <div className="absolute -bottom-6 -right-6 bg-[#efede6] rounded-2xl p-6 shadow-xl max-w-xs">
                  <p className="text-[#202020] text-lg font-medium italic mb-0">Learning to pause and reconnect with love</p>
                </div>
              </div>

              {/* Image 2: Connection */}
              {/* <div className="relative rounded-3xl overflow-hidden shadow-2xl ml-8">
                <img 
                  src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=600&q=80" 
                  alt="Parent and child connecting"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#202020]/40 to-transparent" />
                <div className="absolute bottom-4 right-4 text-[#efede6]">
                  <p className="text-sm opacity-80">Reconnecting with love</p>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
